import { useSteps } from '@features/Steps';
import React, { FC, useEffect, useState } from 'react';
import { IBmoTransactionsGeneralStepProps } from './interfaces';
import classNames from 'classnames';
import { splitCamelCase } from '@shared/utils';
import { Button, TextTooltip } from '@shared/UI';
import { renderEditableField } from './utils/renderEditableField';
import { getEditIcon, getValueIcon, transformDate } from './utils';
import {
    creditTransactionCodes,
    debitTransactionCodes,
} from '@widgets/ISOReportsTable/components/ISOReportsDrawer/components/Transactions/constants';

interface InfoItemProps {
    fieldName: string;
    value: string | undefined | null;
    className?: string;
    keyName: string;
    onChange: (keyName: string, value: string) => void;
}

const fieldDisplayName = {
    Amt: 'Amount',
    'Cd Dbt Ind': 'Credit/Debit',
    'Tx Dt Tm': 'Date',
    Bai: 'BAI',
};

const InfoItem: FC<InfoItemProps> = (props) => {
    const { fieldName, value, className, keyName, onChange } = props;
    const [openEdit, setOpenEdit] = useState(false);

    const onEditClick = () => {
        setOpenEdit((prev) => !prev);
    };

    return (
        <div className={classNames('widget-bmo-transaction-view-drawer__info-item', className)}>
            <span className='widget-bmo-transaction-view-drawer__info-header'>
                {fieldDisplayName[fieldName as keyof typeof fieldDisplayName] || fieldName}:
            </span>
            {fieldName === 'Bai' ? (
                <TextTooltip
                    maxLength={30}
                    text={String(
                        creditTransactionCodes[value as keyof typeof creditTransactionCodes] ||
                            debitTransactionCodes[value as keyof typeof debitTransactionCodes] ||
                            value,
                    )}
                />
            ) : (
                <span
                    className={classNames('widget-bmo-transaction-view-drawer__info-content', value, {
                        nullable: !value,
                    })}
                >
                    {getValueIcon(fieldName, value)}
                    {renderEditableField(openEdit, fieldName, keyName, setOpenEdit, onChange, value)}{' '}
                    {getEditIcon(fieldName, onEditClick)}
                </span>
            )}
        </div>
    );
};

export const GeneralInfo: FC<IBmoTransactionsGeneralStepProps> = ({
    editedValues,
    setEditedValues,
    updateBmoTransaction,
}) => {
    const { pushAllowedStep } = useSteps();
    useEffect(() => {
        pushAllowedStep(0);
        pushAllowedStep(1);
    }, [pushAllowedStep]);

    const onChange = (key: string, value: string | boolean) => {
        setEditedValues({ ...editedValues, [key]: value });
    };

    const keyNames = {
        cusip: 'CUSIP',
        clearingAccount: 'Account Number',
    };

    return (
        <div className='widget-bmo-transaction-view-drawer__content'>
            {Object.keys(editedValues).map((keyName, index) => (
                <InfoItem
                    onChange={onChange}
                    className={classNames({ noBorder: keyName === 'cusip' })}
                    key={index}
                    fieldName={
                        keyNames[keyName as keyof typeof keyNames]
                            ? keyNames[keyName as keyof typeof keyNames]
                            : splitCamelCase(keyName, 'every-word')
                    }
                    value={transformDate(keyName, editedValues) || ''}
                    keyName={keyName}
                />
            ))}
            <div className='widget-bmo-transaction-view-drawer__buttons'>
                <Button onClick={updateBmoTransaction}>Save</Button>
            </div>
        </div>
    );
};
